import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Mail,
  Phone,
  User,
  Clock,
  CheckCircle,
  Eye,
  MessageSquare,
  Trash2,
  Search,
  Filter,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Enquiries = () => {
  const { user } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [notes, setNotes] = useState('');

  const fetchEnquiries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/enquiries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEnquiries(data);
      }
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const updateEnquiryStatus = async (enquiryId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/enquiries/${enquiryId}?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchEnquiries();
      }
    } catch (error) {
      console.error('Error updating enquiry:', error);
    }
  };

  const updateEnquiryNotes = async (enquiryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/enquiries/${enquiryId}?notes=${encodeURIComponent(notes)}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchEnquiries();
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error('Error updating notes:', error);
    }
  };

  const deleteEnquiry = async (enquiryId) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/enquiries/${enquiryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchEnquiries();
        if (selectedEnquiry?.id === enquiryId) {
          setShowDetailModal(false);
          setSelectedEnquiry(null);
        }
      }
    } catch (error) {
      console.error('Error deleting enquiry:', error);
    }
  };

  const openDetailModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setNotes(enquiry.notes || '');
    setShowDetailModal(true);
    
    // Mark as read if new
    if (enquiry.status === 'new') {
      updateEnquiryStatus(enquiry.id, 'read');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      read: 'bg-yellow-100 text-yellow-800',
      responded: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || styles.new;
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    if (filterStatus && enquiry.status !== filterStatus) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        enquiry.name?.toLowerCase().includes(search) ||
        enquiry.email?.toLowerCase().includes(search) ||
        enquiry.phone?.includes(search) ||
        enquiry.subject?.toLowerCase().includes(search) ||
        enquiry.message?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const newCount = enquiries.filter(e => e.status === 'new').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Website Enquiries</h1>
          <p className="text-gray-600">
            Manage enquiries from the website contact form
            {newCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                {newCount} new
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search enquiries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border rounded-lg px-3 py-2"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="responded">Responded</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Enquiries List */}
      <div className="bg-white rounded-lg shadow-sm">
        {filteredEnquiries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No enquiries found</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredEnquiries.map((enquiry) => (
              <div
                key={enquiry.id}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  enquiry.status === 'new' ? 'bg-blue-50' : ''
                }`}
                onClick={() => openDetailModal(enquiry)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-semibold text-gray-900">{enquiry.name}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(enquiry.status)}`}>
                        {enquiry.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                      <span className="flex items-center">
                        <Mail className="w-4 h-4 mr-1" />
                        {enquiry.email}
                      </span>
                      <span className="flex items-center">
                        <Phone className="w-4 h-4 mr-1" />
                        {enquiry.phone}
                      </span>
                    </div>
                    {enquiry.subject && (
                      <p className="text-sm font-medium text-gray-700 mb-1">{enquiry.subject}</p>
                    )}
                    <p className="text-sm text-gray-600 line-clamp-2">{enquiry.message}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xs text-gray-500 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatDate(enquiry.created_at)}
                    </p>
                    {enquiry.responded_at && (
                      <p className="text-xs text-green-600 mt-1">
                        Responded: {formatDate(enquiry.responded_at)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedEnquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Enquiry Details</h2>
                  <p className="text-sm text-gray-500">{formatDate(selectedEnquiry.created_at)}</p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Contact Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Name</p>
                    <p className="font-medium flex items-center">
                      <User className="w-4 h-4 mr-2 text-gray-400" />
                      {selectedEnquiry.name}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Status</p>
                    <span className={`px-2 py-1 text-sm rounded-full ${getStatusBadge(selectedEnquiry.status)}`}>
                      {selectedEnquiry.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Email</p>
                    <a href={`mailto:${selectedEnquiry.email}`} className="font-medium text-blue-600 hover:underline flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      {selectedEnquiry.email}
                    </a>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Phone</p>
                    <a href={`tel:${selectedEnquiry.phone}`} className="font-medium text-blue-600 hover:underline flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      {selectedEnquiry.phone}
                    </a>
                  </div>
                </div>
              </div>

              {/* Subject */}
              {selectedEnquiry.subject && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase mb-1">Subject</p>
                  <p className="font-medium text-gray-900">{selectedEnquiry.subject}</p>
                </div>
              )}

              {/* Message */}
              <div className="mb-6">
                <p className="text-xs text-gray-500 uppercase mb-1">Message</p>
                <div className="bg-white border rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedEnquiry.message}</p>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <p className="text-xs text-gray-500 uppercase mb-1">Internal Notes</p>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this enquiry..."
                  className="w-full border rounded-lg p-3 h-24 resize-none"
                />
                <button
                  onClick={() => updateEnquiryNotes(selectedEnquiry.id)}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  Save Notes
                </button>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <button
                  onClick={() => updateEnquiryStatus(selectedEnquiry.id, 'read')}
                  className={`px-4 py-2 rounded-lg text-sm flex items-center ${
                    selectedEnquiry.status === 'read' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                  }`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Mark Read
                </button>
                <button
                  onClick={() => updateEnquiryStatus(selectedEnquiry.id, 'responded')}
                  className={`px-4 py-2 rounded-lg text-sm flex items-center ${
                    selectedEnquiry.status === 'responded' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Mark Responded
                </button>
                <button
                  onClick={() => updateEnquiryStatus(selectedEnquiry.id, 'closed')}
                  className={`px-4 py-2 rounded-lg text-sm flex items-center ${
                    selectedEnquiry.status === 'closed' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Close
                </button>
                {user?.role === 'partner' && (
                  <button
                    onClick={() => deleteEnquiry(selectedEnquiry.id)}
                    className="px-4 py-2 rounded-lg text-sm flex items-center bg-red-100 text-red-800 hover:bg-red-200 ml-auto"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enquiries;
